import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";

interface OrgNode {
  id: string;
  name: string;
  function: string | null;
  region: string | null;
  status_counts: {
    home: number;
    relocated: number;
    in_transit: number;
    unknown: number;
    total: number;
  };
  children: OrgNode[];
}

export default function OrgTreePage() {
  const q = useQuery({
    queryKey: ["org-tree"],
    queryFn: () => api.get<{ roots: OrgNode[] }>("/api/org/tree"),
  });

  if (q.isPending) return <div className="text-slate-400">Loading…</div>;
  if (!q.data) return <div className="text-red-300">Failed to load org tree.</div>;

  return (
    <div className="space-y-3 sm:space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-100">
          Org tree
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          Status overlay across functions. Add sub-units to see deeper hierarchy.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
        {q.data.roots.map((node) => (
          <OrgNodeCard key={node.id} node={node} />
        ))}
      </div>
    </div>
  );
}

function OrgNodeCard({ node }: { node: OrgNode }) {
  const c = node.status_counts;
  const max = Math.max(c.total, 1);
  return (
    <div className="rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-slate-100 font-medium">{node.name}</div>
        <div className="text-xs text-slate-500">{c.total} ppl</div>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-800/60">
        <div className="bg-emerald-400" style={{ width: `${(c.home / max) * 100}%` }} />
        <div className="bg-amber-400" style={{ width: `${(c.relocated / max) * 100}%` }} />
        <div className="bg-sky-400" style={{ width: `${(c.in_transit / max) * 100}%` }} />
        <div className="bg-slate-600" style={{ width: `${(c.unknown / max) * 100}%` }} />
      </div>
      <div className="grid grid-cols-4 gap-1 text-[11px] text-slate-400">
        <Cell color="text-emerald-300" label="Home" value={c.home} />
        <Cell color="text-amber-300" label="Reloc." value={c.relocated} />
        <Cell color="text-sky-300" label="Transit" value={c.in_transit} />
        <Cell color="text-slate-400" label="Unknown" value={c.unknown} />
      </div>
      {node.children.length > 0 && (
        <div className="pt-2 border-t border-slate-800/60 space-y-1">
          {node.children.map((child) => (
            <div key={child.id} className="text-xs text-slate-400 flex justify-between">
              <span>{child.name}</span>
              <span>{child.status_counts.total}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Cell({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className={`tabular-nums ${color}`}>
      <span className="text-slate-200 font-medium">{value}</span>{" "}
      <span className="text-slate-500">{label}</span>
    </div>
  );
}
